import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { Page } from './types';

/**
 * Cliente de las solicitudes del equipo (espejo de `apps/api/src/schemas/requests.ts`, ADR-0021): `GET|POST /admin/tenants/:tenantId/requests*` y el enlace público de la persona
 * (`/admin/appointments/*`). Se refresca cada 15 s solo con la pestaña visible. Los datos de la persona nunca salen de la caché de Query (nada en localStorage ni en la URL).
 */
export const REQUESTS_REFRESH_MS = 15_000;

export type RequestStatus =
  | 'new'
  | 'taken'
  | 'pending_confirmation'
  | 'confirmed'
  | 'proposed'
  | 'closed'
  | 'cancelled';
export type RequestPriority = 'high' | 'medium' | 'low';
export type ProposalStatus = 'open' | 'accepted' | 'rejected' | 'expired' | 'superseded';

export interface RequestItem {
  id: string;
  kind: string;
  status: RequestStatus;
  priority: RequestPriority | null;
  summary: string;
  customerName: string | null;
  scope: Record<string, string>;
  assignedUserId: string | null;
  assignedName: string | null;
  slotStart: string | null;
  slotLocation: string | null;
  isTest: boolean;
  createdAt: string;
  updatedAt: string;
}
export interface RequestEventView {
  fromStatus: RequestStatus | null;
  toStatus: RequestStatus;
  actorKind: 'agent' | 'user' | 'customer' | 'system';
  actorName: string | null;
  note: string;
  createdAt: string;
}
export interface RequestDetail extends RequestItem {
  conversationId: string;
  customer: { name?: string; email?: string; phone?: string };
  details: Record<string, string>;
  notifyCustomer: boolean;
  takenAt: string | null;
  closedAt: string | null;
  booking: {
    id: string;
    status: 'pending' | 'confirmed' | 'proposed' | 'cancelled';
    start: string;
    end: string;
    location: string | null;
    pendingUntil: string | null;
  } | null;
  proposals: {
    id: string;
    status: ProposalStatus;
    slotStart: string;
    slotEnd: string;
    location: string | null;
    note: string;
    expiresAt: string;
    createdAt: string;
  }[];
  events: RequestEventView[];
  allowed: RequestStatus[];
}
export interface RequestSummary {
  new: number;
  taken: number;
  pendingConfirmation: number;
  proposed: number;
  confirmed: number;
  mine: number;
  unassigned: number;
}

/** Vistas rápidas de la lista (cada una es un filtro de la API). */
export const REQUEST_VIEWS = ['open', 'new', 'pending', 'mine', 'unassigned', 'closed'] as const;
export type RequestView = (typeof REQUEST_VIEWS)[number];
export interface RequestFilters {
  view: RequestView;
  kind?: string | undefined;
  priority?: RequestPriority | undefined;
}

const root = (tenantId: string) => ['tenants', tenantId, 'requests'] as const;

function queryOf(f: RequestFilters): Record<string, string | undefined> {
  const q: Record<string, string | undefined> = { kind: f.kind, priority: f.priority, limit: '50' };
  if (f.view === 'new') q.status = 'new';
  else if (f.view === 'pending') q.status = 'pending_confirmation,proposed';
  else if (f.view === 'closed') q.status = 'closed,cancelled';
  else if (f.view === 'mine') q.assigned = 'me';
  else if (f.view === 'unassigned') q.assigned = 'none';
  return q;
}

export const requestsListQuery = (tenantId: string, f: RequestFilters, cursor?: string) =>
  queryOptions({
    queryKey: [...root(tenantId), 'list', f.view, f.kind ?? null, f.priority ?? null, cursor ?? null],
    queryFn: ({ signal }) =>
      api.get<Page<RequestItem>>(`/admin/tenants/${tenantId}/requests`, { ...queryOf(f), cursor }, signal),
    refetchInterval: REQUESTS_REFRESH_MS,
    refetchIntervalInBackground: false,
    placeholderData: keepPreviousData,
  });

export const requestsSummaryQuery = (tenantId: string) =>
  queryOptions({
    queryKey: [...root(tenantId), 'summary'],
    queryFn: ({ signal }) => api.get<RequestSummary>(`/admin/tenants/${tenantId}/requests/summary`, undefined, signal),
    refetchInterval: REQUESTS_REFRESH_MS,
    refetchIntervalInBackground: false,
  });

/** El detalle audita cada lectura: no se relee al volver a la pestaña (se refresca tras cada acción). */
export const requestDetailQuery = (tenantId: string, requestId: string) =>
  queryOptions({
    queryKey: [...root(tenantId), 'detail', requestId],
    queryFn: ({ signal }) =>
      api.get<RequestDetail>(`/admin/tenants/${tenantId}/requests/${requestId}`, undefined, signal),
    refetchOnWindowFocus: false,
  });

export const requestSlotsQuery = (tenantId: string, requestId: string) =>
  queryOptions({
    queryKey: [...root(tenantId), 'slots', requestId],
    queryFn: ({ signal }) =>
      api.get<{ slots: { start: string; end: string; location: string | null; label: string | null }[] }>(
        `/admin/tenants/${tenantId}/requests/${requestId}/slots`,
        undefined,
        signal,
      ),
    refetchOnWindowFocus: false,
  });

export type RequestAction =
  | { type: 'take' }
  | { type: 'release' }
  | { type: 'assign'; userId: string | null }
  | { type: 'priority'; priority: RequestPriority | null }
  | { type: 'confirm'; note?: string | undefined }
  | { type: 'cancel'; note?: string | undefined }
  | { type: 'close'; note?: string | undefined }
  | { type: 'propose'; start: string; end: string; location?: string | undefined; note?: string | undefined };

export function useRequestAction(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ requestId, action }: { requestId: string; action: RequestAction }) => {
      const { type, ...body } = action;
      return api.post<{ ok: true; status: RequestStatus }>(
        `/admin/tenants/${tenantId}/requests/${requestId}/${type}`,
        type === 'take' || type === 'release' ? undefined : body,
      );
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: root(tenantId) }),
  });
}

// ---- Enlace público de la persona (sin sesión) -----------------------------------------------------------------------------------------------------------------

export interface AppointmentPreview {
  status: ProposalStatus;
  brandName: string;
  original: { start: string; end: string; location: string | null } | null;
  proposed: { start: string; end: string; location: string | null };
  note: string;
  expiresAt: string;
}
export type AppointmentResult =
  | { result: 'accepted' | 'rejected' | 'expired' | 'slot_unavailable' }
  | { result: 'already_resolved'; status: ProposalStatus };

export const previewAppointment = (token: string) =>
  api.post<AppointmentPreview>('/admin/appointments/preview', { token });
export const respondAppointment = (token: string, decision: 'accept' | 'reject') =>
  api.post<AppointmentResult>('/admin/appointments/respond', { token, decision });
